import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Heart, Minus, Plus, Truck, ShieldCheck } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Reveal } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Product() {
  const { slug } = useParams();
  const { storefront, money, addToCart, toggleWishlist, inWishlist } = useApp();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const [related, setRelated] = useState([]);
  const [heroWide, setHeroWide] = useState(false);
  const [selOpts, setSelOpts] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/storefront/${storefront}/product/${slug}`).then((r) => {
      setP(r.data); setActive(0); setSize(null); setColor(null); setQty(1);
      const defs = {}; (r.data.option_groups || []).forEach((g) => { const d = (g.choices || []).find((c) => c.default); if (d) defs[g.id || g.heading] = d.name; }); setSelOpts(defs);
      if (r.data.orientation === "landscape") setHeroWide(true);
      if (r.data.collections?.[0]) api.get(`/storefront/${storefront}/products`, { params: { collection: r.data.collections[0] } })
        .then((rr) => setRelated(rr.data.filter((x) => x.id !== r.data.id).slice(0, 4)));
    }).catch(() => setP(false));
  }, [storefront, slug]);

  if (p === false) return <div className="text-center py-40 font-serif text-3xl">Piece not found</div>;
  if (!p) return <div className="h-[60vh]" />;

  const add = () => {
    if (p.inventory_qty <= 0) return;
    if (p.sizes?.length && !size) return toast.error("Please select a size.");
    if (p.colors?.length && !color) return toast.error("Please select a colour.");
    if (missingRequired) return toast.error("Please complete the required options.");
    addToCart(p, { size, color, qty, options: buildOptions(), unitPrice: total });
  };

  const groups = (p.option_groups || []).filter((g) => g.enabled !== false);
  const base = p.sale_price_usd || p.price_usd || 0;
  const keyOf = (g) => g.id || g.heading;
  const adj = groups.reduce((s, g) => { const ch = (g.choices || []).find((c) => c.name === selOpts[keyOf(g)]); return s + (ch ? Number(ch.price_usd || 0) : 0); }, 0);
  const total = Math.max(0, base + adj);
  const buildOptions = () => groups.map((g) => { const ch = (g.choices || []).find((c) => c.name === selOpts[keyOf(g)]); return ch ? { group: keyOf(g), choice: ch.name, price_usd: Number(ch.price_usd || 0) } : null; }).filter(Boolean);
  const missingRequired = groups.some((g) => g.required && !selOpts[keyOf(g)]);
  const money2 = (n) => money(n);

  const src = p.images?.[active];
  if (src && typeof window !== "undefined") {
    const im = new window.Image();
    im.onload = () => setHeroWide(im.naturalWidth / im.naturalHeight > 1.2);
    im.src = src;
  }

  return (
    <div>
      {heroWide && (
        <section className="w-full bg-ev-bg">
          <img src={p.images?.[active]} alt={p.name} className="w-full h-auto block" />
          {p.images?.length > 1 && (
            <div className="flex justify-center gap-3 py-5">
              {p.images.map((img, i) => (
                <button key={i} onClick={() => setActive(i)} className="w-16 h-16 overflow-hidden border"
                  style={{ borderColor: active === i ? "var(--ev-text)" : "var(--ev-border)" }}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </section>
      )}
      <div className={heroWide ? "max-w-[1000px] mx-auto px-6 py-12" : "max-w-[1500px] mx-auto px-6 py-10 grid md:grid-cols-2 gap-12 lg:gap-20"}>
        {!heroWide && (
        <div className="flex gap-4">
          {p.images?.length > 1 && (
            <div className="hidden md:flex flex-col gap-3 w-20">
              {p.images.map((img, i) => (
                <button key={i} onClick={() => setActive(i)} className="aspect-[3/4] overflow-hidden border"
                  style={{ borderColor: active === i ? "var(--ev-text)" : "var(--ev-border)" }}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <div className="flex-1 aspect-[3/4] bg-ev-bg overflow-hidden">
            <img src={p.images?.[active]} alt={p.name} className="w-full h-full object-cover" />
          </div>
        </div>
        )}

        <div className="md:py-6">
          <div className="eyebrow mb-3" style={{ color: "var(--ev-muted)" }}>{p.category}</div>
          <h1 className="font-serif text-3xl md:text-4xl mb-4">{p.name}</h1>
          <div className="text-xl mb-6">
            {p.pricing_mode === "range" && (p.price_min_usd || p.price_max_usd) ? (
              <span>{money(p.price_min_usd || base)} – {money(p.price_max_usd || base)}</span>
            ) : groups.length ? (
              <span data-testid="pdp-live-price">{money(total)}</span>
            ) : p.pricing_mode === "from" ? (
              <span>From {money(base)}</span>
            ) : p.sale_price_usd ? (
              <span><span className="line-through opacity-50 mr-3">{money(p.price_usd)}</span>
                <span style={{ color: "var(--ev-accent)" }}>{money(p.sale_price_usd)}</span></span>
            ) : money(p.price_usd)}
          </div>

          {groups.map((g) => (
            <div key={keyOf(g)} className="mb-6" data-testid={`opt-group-${keyOf(g)}`}>
              <div className="eyebrow mb-1">{g.heading}{g.required && <span style={{ color: "var(--ev-accent)" }}> *</span>}</div>
              {g.description && <p className="text-xs mb-2" style={{ color: "var(--ev-muted)" }}>{g.description}</p>}
              {g.type === "dropdown" ? (
                <select data-testid={`opt-select-${keyOf(g)}`} value={selOpts[keyOf(g)] || ""} onChange={(e) => setSelOpts({ ...selOpts, [keyOf(g)]: e.target.value })}
                  className="w-full h-11 px-3 border bg-transparent text-sm" style={{ borderColor: "var(--ev-border)" }}>
                  <option value="">Select…</option>
                  {(g.choices || []).map((c) => <option key={c.name} value={c.name}>{c.name}{c.price_usd ? ` (+${money(c.price_usd)})` : ""}</option>)}
                </select>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(g.choices || []).map((c) => {
                    const sel = selOpts[keyOf(g)] === c.name;
                    return (
                      <button key={c.name} data-testid={`opt-choice-${keyOf(g)}-${c.name}`} onClick={() => setSelOpts({ ...selOpts, [keyOf(g)]: c.name })}
                        className="px-4 h-11 border text-sm flex items-center gap-2 transition-colors"
                        style={{ borderColor: sel ? "var(--ev-text)" : "var(--ev-border)", background: sel ? "var(--ev-text)" : "transparent", color: sel ? "#fff" : "var(--ev-text)" }}>
                        {c.image && <img src={c.image} alt="" className="w-5 h-5 object-cover rounded-full" />}
                        {c.name}{c.price_usd ? <span className="opacity-70">+{money(c.price_usd)}</span> : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
          <p className="text-base leading-relaxed mb-8" style={{ color: "var(--ev-muted)" }}>{p.description}</p>

          {p.sizes?.length > 0 && (
            <div className="mb-6">
              <div className="eyebrow mb-3">Size</div>
              <div className="flex flex-wrap gap-2">
                {p.sizes.map((s) => (
                  <button key={s} data-testid={`size-${s}`} onClick={() => setSize(s)}
                    className="min-w-[48px] px-3 h-11 border text-sm transition-colors"
                    style={{ borderColor: size === s ? "var(--ev-text)" : "var(--ev-border)", background: size === s ? "var(--ev-text)" : "transparent", color: size === s ? "#fff" : "var(--ev-text)" }}>{s}</button>
                ))}
              </div>
            </div>
          )}
          {p.colors?.length > 0 && (
            <div className="mb-6">
              <div className="eyebrow mb-3">Colour</div>
              <div className="flex flex-wrap gap-2">
                {p.colors.map((c) => (
                  <button key={c} data-testid={`color-${c}`} onClick={() => setColor(c)}
                    className="px-4 h-11 border text-sm transition-colors"
                    style={{ borderColor: color === c ? "var(--ev-text)" : "var(--ev-border)", background: color === c ? "var(--ev-text)" : "transparent", color: color === c ? "#fff" : "var(--ev-text)" }}>{c}</button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center border h-12" style={{ borderColor: "var(--ev-border)" }}>
              <button className="px-3 h-full" onClick={() => setQty(Math.max(1, qty - 1))}><Minus size={14} /></button>
              <span className="px-4 text-sm">{qty}</span>
              <button className="px-3 h-full" onClick={() => setQty(qty + 1)}><Plus size={14} /></button>
            </div>
            <button data-testid="pdp-add-to-cart" onClick={add} disabled={p.inventory_qty <= 0}
              className="btn-luxe flex-1" style={p.inventory_qty <= 0 ? { opacity: 0.5, cursor: "not-allowed" } : {}}>
              {p.inventory_qty <= 0 ? "Sold Out" : "Add to Bag"}
            </button>
            <button data-testid="pdp-wishlist" onClick={() => toggleWishlist(p)} className="w-12 h-12 border flex items-center justify-center" style={{ borderColor: "var(--ev-border)" }}>
              <Heart size={18} strokeWidth={1.4} fill={inWishlist(p.id) ? "var(--ev-accent)" : "none"} color={inWishlist(p.id) ? "var(--ev-accent)" : "currentColor"} />
            </button>
          </div>

          {p.inventory_qty <= 0 && (
            <p className="text-sm mb-6 uppercase tracking-wide" style={{ color: "var(--ev-muted)" }}>Sold Out</p>
          )}

          <div className="space-y-3 border-t pt-6 text-sm" style={{ borderColor: "var(--ev-border)", color: "var(--ev-muted)" }}>
            {p.materials && <div><strong className="font-medium text-[var(--ev-text)]">Materials:</strong> {p.materials}</div>}
            <div className="flex items-center gap-2"><Truck size={16} strokeWidth={1.4} /> Complimentary shipping on all orders</div>
            <div className="flex items-center gap-2"><ShieldCheck size={16} strokeWidth={1.4} /> Crafted with Maison Évaren savoir-faire</div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="max-w-[1500px] mx-auto px-6 py-20">
          <h2 className="font-serif text-3xl text-center mb-12">You May Also Admire</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
            {related.map((r) => <ProductCard key={r.id} product={r} />)}
          </div>
        </section>
      )}
    </div>
  );
}
